import { useState } from "react";
import { Link } from "react-router-dom";
import { Building2, DoorOpen, Flag, Pencil, UserRoundCheck, UserRoundPlus } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const ROLE_SHORT = { staffing_coordinator: "Staffing coordinator", operations: "Operations" };

function ControlCard({ icon: Icon, title, children, testId, highlight }) {
  return (
    <section className={`drawer-control ${highlight ? "drawer-control-highlight" : ""}`} data-testid={testId}>
      <h3><Icon size={14} /> {title}</h3>
      {children}
    </section>
  );
}

export const DrawerControls = ({ candidates, focus, meta, providers, request, onChanged }) => {
  const [recruiter, setRecruiter] = useState(request.recruiter_id || "");
  const [priority, setPriority] = useState(request.priority);
  const [client, setClient] = useState(request.client_id || "");
  const [fill, setFill] = useState({ person_type: "candidate", person_id: "", start_date: "", end_date: "", note: "" });
  const [edit, setEdit] = useState({ role_name: request.role_name, needed_date: request.needed_date || "", hours: request.hours || 1, notes: request.notes || "" });
  const [vacateReason, setVacateReason] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  async function run(name, action, message) {
    setBusy(name);
    setError("");
    try {
      await action();
      onChanged(message);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy("");
    }
  }

  const bulk = (payload) => api.post("/staffing-board/bulk-update", { request_ids: [request.id], ...payload });
  const people = fill.person_type === "candidate" ? candidates : providers;

  return (
    <div className="drawer-controls" data-testid="drawer-controls">
      {error && <p className="form-message error-message" data-testid="drawer-control-error">{error}</p>}
      <ControlCard icon={UserRoundPlus} title="Recruiter" testId="drawer-recruiter-control" highlight={focus === "recruiter"}>
        {meta.recruiters.length === 0 ? (
          <p className="pairing-meta" data-testid="drawer-recruiter-empty">No eligible active recruiters. Active recruiters, staffing coordinators and operations users appear here — <Link to="/team" data-testid="drawer-recruiter-team-link">open the Team directory</Link>.</p>
        ) : (
        <div className="drawer-inline-form">
          <select data-testid="drawer-recruiter-select" value={recruiter} onChange={(event) => setRecruiter(event.target.value)}>
            <option value="">Select recruiter</option>
            {meta.recruiters.map((item) => <option key={item.id} value={item.id}>{item.name}{item.role && item.role !== "recruiter" ? ` (${ROLE_SHORT[item.role] || item.role})` : ""}</option>)}
          </select>
          <button className="button button-primary" data-testid="drawer-recruiter-save" disabled={!recruiter || recruiter === request.recruiter_id || busy === "recruiter"} type="button" onClick={() => run("recruiter", () => bulk({ recruiter_id: recruiter }), "Recruiter assigned.")}>Assign</button>
        </div>
        )}
      </ControlCard>
      <ControlCard icon={Flag} title="Priority" testId="drawer-priority-control">
        <div className="drawer-inline-form">
          <select data-testid="drawer-priority-select" value={priority} onChange={(event) => setPriority(event.target.value)}>
            {["Urgent", "High", "Standard"].map((value) => <option key={value}>{value}</option>)}
          </select>
          <button className="button button-primary" data-testid="drawer-priority-save" disabled={priority === request.priority || busy === "priority"} type="button" onClick={() => run("priority", () => bulk({ priority }), `Priority set to ${priority}.`)}>Update</button>
        </div>
      </ControlCard>
      <ControlCard icon={Building2} title="Client" testId="drawer-client-control" highlight={focus === "client"}>
        <div className="drawer-inline-form">
          <select data-testid="drawer-client-select" value={client} onChange={(event) => setClient(event.target.value)}>
            <option value="">Select client</option>
            {meta.clients.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
          <button className="button button-primary" data-testid="drawer-client-save" disabled={!client || client === request.client_id || busy === "client"} type="button" onClick={() => run("client", () => bulk({ client_id: client }), "Client linked.")}>Link</button>
        </div>
      </ControlCard>
      {request.status !== "Filled" && (
        <ControlCard icon={UserRoundCheck} title="Assign candidate and fill" testId="drawer-fill-control" highlight={focus === "fill"}>
          <form className="drawer-form-grid" onSubmit={(event) => { event.preventDefault(); run("fill", () => api.post(`/requests/${request.id}/fill`, fill), "Position filled."); }}>
            <label>Person type
              <select data-testid="drawer-fill-person-type" value={fill.person_type} onChange={(event) => setFill({ ...fill, person_type: event.target.value, person_id: "" })}>
                <option value="candidate">Candidate</option>
                <option value="provider">Provider</option>
              </select>
            </label>
            <label>Person
              <select data-testid="drawer-fill-person" required value={fill.person_id} onChange={(event) => setFill({ ...fill, person_id: event.target.value })}>
                <option value="">Select person</option>
                {people.map((person) => <option key={person.id} value={person.id}>{person.first_name} {person.last_name}</option>)}
              </select>
            </label>
            <label>Start date<input data-testid="drawer-fill-start" required type="date" value={fill.start_date} onChange={(event) => setFill({ ...fill, start_date: event.target.value })} /></label>
            <label>End date<input data-testid="drawer-fill-end" type="date" value={fill.end_date} onChange={(event) => setFill({ ...fill, end_date: event.target.value })} /></label>
            <label className="drawer-form-full">Note<textarea data-testid="drawer-fill-note" rows="2" value={fill.note} onChange={(event) => setFill({ ...fill, note: event.target.value })} /></label>
            <button className="button button-primary drawer-form-full" data-testid="drawer-fill-save" disabled={busy === "fill"} type="submit"><UserRoundCheck size={15} /> Assign and mark filled</button>
          </form>
        </ControlCard>
      )}
      {request.status === "Filled" && (
        <ControlCard icon={DoorOpen} title="Vacate position" testId="drawer-vacate-control" highlight={focus === "vacate"}>
          <form className="drawer-form-grid" onSubmit={(event) => { event.preventDefault(); run("vacate", () => api.post(`/staffing-board/${request.id}/vacate`, { reason: vacateReason }), "Position vacated and reopened — age now counts from today."); }}>
            <label className="drawer-form-full">Reason (required)<textarea data-testid="drawer-vacate-reason" required minLength={3} rows="2" placeholder="e.g. Provider resigned effective Nov 1" value={vacateReason} onChange={(event) => setVacateReason(event.target.value)} /></label>
            <button className="button button-secondary drawer-form-full" data-testid="drawer-vacate-save" disabled={busy === "vacate" || vacateReason.trim().length < 3} type="submit"><DoorOpen size={15} /> Vacate and reopen</button>
          </form>
        </ControlCard>
      )}
      <ControlCard icon={Pencil} title="Edit details" testId="drawer-edit-control">
        <form className="drawer-form-grid" onSubmit={(event) => { event.preventDefault(); run("edit", () => api.put(`/requests/${request.id}`, { ...edit, hours: Number(edit.hours) }), "Request details saved."); }}>
          <label className="drawer-form-full">Role<input data-testid="drawer-edit-role" required value={edit.role_name} onChange={(event) => setEdit({ ...edit, role_name: event.target.value })} /></label>
          <label>Date needed<input data-testid="drawer-edit-date" type="date" value={edit.needed_date} onChange={(event) => setEdit({ ...edit, needed_date: event.target.value })} /></label>
          <label>Weekly hours<input data-testid="drawer-edit-hours" min="1" type="number" value={edit.hours} onChange={(event) => setEdit({ ...edit, hours: event.target.value })} /></label>
          <label className="drawer-form-full">Notes<textarea data-testid="drawer-edit-notes" rows="2" value={edit.notes} onChange={(event) => setEdit({ ...edit, notes: event.target.value })} /></label>
          <button className="button button-secondary drawer-form-full" data-testid="drawer-edit-save" disabled={busy === "edit"} type="submit">Save details</button>
        </form>
      </ControlCard>
    </div>
  );
};
